import { IDrawCurvedLineOptions, IDrawLineOptions, IGridInfo } from './lineChart.types'
import dataUtils from '@/services/data/data.utils.ts'

const SEC = 1000
const MIN = 60 * SEC
const HOUR = 60 * MIN
const DAY = 24 * HOUR

// предпочтительные "круглые" значения для ширины шага сетки по оси времени
const steps = [DAY, HOUR, 10 * MIN, MIN, 10 * SEC, SEC, 100, 10, 1, 0.1]

const calcTimeStep = (frame: number, t0: number, lines: number) => {
  // вычисляем ширину шага при котором на график войдут примерно *lines* линий сетки
  const baseStep = steps.find((v) => 2 * v < frame)
  const count = Math.ceil(frame / (baseStep * lines))
  const step = baseStep * count

  // вычисляем время первой линии сетки (минимальное круглое число кратное шагу и большее чем t0)
  let dayStart = new Date(t0).setHours(0)
  dayStart = dayStart - (dayStart % HOUR)
  const start = dayStart + step * Math.ceil((t0 - dayStart) / step)

  return { step, start }
}

const formatTime = (ts: number, frame: number) => {
  const d = new Date(ts)

  if (frame < 5) return formatMicro(d, ts)
  else if (frame < 5 * SEC) return formatMilli(d)
  else if (frame < 5 * MIN) return pad`${d.getMinutes()}:${d.getSeconds()}`
  else if (frame < DAY) return pad`${d.getHours()}:${d.getMinutes()}`
  else if (frame < 7 * DAY) return pad`${d.getDate()}.${d.getMonth() + 1} ${d.getHours()}:${d.getMinutes()}`
  else return pad`${d.getDate()}.${d.getMonth() + 1}`
}

const formatMicro = (d: Date, ts: number) => {
  const us = Math.round((ts * 1000) % 1000)
  const ms = d.getMilliseconds()
  const s = d.getSeconds()

  return `${zeropad(s)}:${zeropad3(ms)}.${zeropad3(us)}`
}

const formatMilli = (d: Date) => {
  const ms = d.getMilliseconds()
  const s = d.getSeconds()

  return `${zeropad(s)}:${zeropad3(ms)}`
}

const zeropad = (n: number) => (n < 10 ? '0' + n : '' + n)

const zeropad3 = (t: number) => (t < 10 ? '00' + t : t < 100 ? '0' + t : '' + t)

const pad = (parts: TemplateStringsArray, ...values: number[]) => {
  let result = parts[0]

  for (let i = 1; i < parts.length; i++) {
    result += zeropad(values[i - 1]) + parts[i]
  }

  return result
}

const help = {
  en: ['sec:ms.μs', 'sec:ms', 'min:sec', 'hr:min', 'date hr:min', 'date'],
  ru: ['сек:мс.мкс', 'сек:мс', 'мин:сек', 'час:мин', 'дата час:мин', 'дата'],
}

// подсказка по формату выводимого времени
const formatTimeHelp = (frame: number, lang = 'ru') => {
  if (frame < 5) return help[lang][0]
  else if (frame < 5 * SEC) return help[lang][1]
  else if (frame < 5 * MIN) return help[lang][2]
  else if (frame < DAY) return help[lang][3]
  else if (frame < 7 * DAY) return help[lang][4]
  else return help[lang][5]
}

/**
 * Округлить число вверх до второй значащей цифры делящейся на 5
 */
const roundStep = (x: number): number => {
  if (x <= 0 || !x) return 0

  let multiplier = 1
  let step = x

  while (step < 10 || step > 100) {
    const factor = step < 10 ? 10 : 0.1

    step *= factor
    multiplier *= factor
  }

  const result = (5 * Math.ceil(step / 5)) / multiplier
  return result > 10 ? Math.round(result) : result
}

const calcYGrid = (minY: number, maxY: number, maxLines: number): IGridInfo => {
  const step = roundStep((maxY - minY) / maxLines)

  const values: number[] = []
  const result = { step, values }
  if (!step) return result

  let value = minY - (minY % step)
  while (value < maxY) {
    if (value > minY) values.push(value)
    value += step
  }

  return result
}

/**
 * Определить порядок первой значащей цифры указанного числа
 */
const getNumberPrecision = (x: number) => {
  if (!x || x < 0 || x > 1) return 0

  let precision = 1
  let scaled = x

  while (scaled < 0.1) {
    precision += 1
    scaled *= 10
  }

  return precision
}

const drawLine = (options: IDrawLineOptions) => {
  const { ctx, data, xScale, yScale } = options
  ctx.strokeStyle = options.color
  ctx.lineWidth = 2
  ctx.lineJoin = 'round'

  // максимальное расстояние между двумя точками которое соединять линией
  const DELTA = options.disconnectDistance || Number.MAX_SAFE_INTEGER

  // расстояние после которого стоит провести линию в случае если диконект не задан
  const DELTA_CONNECT = 50

  let isStart = true
  let prevX = 0
  let prevY = 0

  ctx.beginPath()

  let dataToIterate = data
  if (options.removeExtreme) {
    dataToIterate = dataUtils.clearDiscredetationErrors(data)
  }

  let i = 0
  for (const { ts, value } of dataToIterate) {
    const x = xScale(ts)
    const y = yScale(value)

    // Пропускаем следующую точку если разница для глаза незаметна
    if (Math.abs(x - prevX) < 0.25) {
      continue
    }

    if (isStart) {
      ctx.moveTo(x, y)
      isStart = false

      prevX = x
      prevY = y

      continue
    }

    if (Math.abs(prevX - x) > DELTA) {
      ctx.moveTo(x, y)
    } else {
      if (Math.abs(prevX - x) > DELTA_CONNECT && options.mode !== 'step') {
        ctx.lineTo(x, prevY)
      }

      if (options.mode === 'step') {
        ctx.lineTo(x, prevY)
        ctx.lineTo(x, y)
      } else {
        ctx.lineTo(x, y)
      }
    }

    prevX = x
    prevY = y
    i++
  }

  ctx.stroke()
  ctx.closePath()
  ctx.lineJoin = undefined
}

const drawCurvedLine = (options: IDrawCurvedLineOptions) => {
  const { ctx, data, xScale, yScale } = options
  ctx.lineWidth = 2
  ctx.strokeStyle = options.color
  ctx.lineJoin = 'round'

  // Переходим к начальной точке
  ctx.beginPath()
  ctx.setLineDash([])

  const firstPoint = data[0]
  if (!firstPoint) return

  const firstX = xScale(firstPoint.ts)
  const firstY = yScale(firstPoint.value)
  ctx.moveTo(firstX, firstY)

  // Рисуем график и сглаживанием линии
  for (let i = 0; i < data.length - 1; ++i) {
    const point0 = { x: xScale(data[i].ts), y: yScale(data[i].value) }
    const point1 = { x: xScale(data[i + 1].ts), y: yScale(data[i + 1].value) }

    const x_mid = (point0.x + point1.x) / 2
    const y_mid = (point0.y + point1.y) / 2
    const cp_x1 = (x_mid + point0.x) / 2
    const cp_x2 = (x_mid + point1.x) / 2

    ctx.quadraticCurveTo(cp_x1, point0.y, x_mid, y_mid)
    ctx.quadraticCurveTo(cp_x2, point1.y, point1.x, point1.y)
  }

  ctx.stroke()
  ctx.closePath()
  ctx.lineJoin = undefined
}

export type ChartScale = 1e-6 | 1e-3 | 1 | 1e3 | 1e6 | 1e9
const scales: ChartScale[] = [1e9, 1e6, 1e3, 1, 1e-3, 1e-6]

const lineChartCoreUtils = {
  calcTimeStep,
  formatTime,
  formatTimeHelp,
  calcYGrid,
  getNumberPrecision,
  scales,
  drawLine,
  drawCurvedLine,
}
export default lineChartCoreUtils
